/**
 * The platform error envelope (design/07 §1, `shop_common.errors`):
 * `{ error: { code, message, messageKey, requestId, details[] } }`. Every admin API call that fails
 * becomes an `ApiError`; the UI never shows `message` (English, for logs) — it translates `code`
 * through the error dictionary (src/lib/errors).
 */
import { rememberRequestId } from "./lastRequestId";

export interface ApiErrorDetail {
  /** Field path in camelCase, e.g. `price` or `variants.0.price` (see `detailField`). */
  field?: string;
  loc?: Array<string | number>;
  code?: string;
  type?: string;
  message?: string;
  msg?: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  constructor(
    /** HTTP status; `0` = no answer (offline, DNS, CORS). */
    public readonly status: number,
    public readonly code: string,
    public readonly details: ApiErrorDetail[] = [],
    public readonly requestId: string | null = null,
    /** Seconds from `Retry-After` (429 / 423). */
    public readonly retryAfter: number | null = null,
  ) {
    super(`${String(status)} ${code}`);
    this.name = "ApiError";
  }
}

/** Anything that looks like an API error (ours or the T09 screens' own class). */
export interface ApiErrorLike {
  status: number;
  code: string;
  details?: unknown;
  requestId?: string | null;
  retryAfter?: number | null;
}

export function isApiErrorLike(value: unknown): value is ApiErrorLike {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { status?: unknown }).status === "number" &&
    typeof (value as { code?: unknown }).code === "string"
  );
}

interface EnvelopeBody {
  error?: {
    code?: unknown;
    requestId?: unknown;
    details?: unknown;
  };
  detail?: unknown;
}

function statusCode(status: number): string {
  if (status === 401) {
    return "unauthorized";
  }
  if (status === 403) {
    return "forbidden";
  }
  if (status === 404) {
    return "not_found";
  }
  if (status === 409) {
    return "conflict";
  }
  if (status === 422 || status === 400) {
    return "validation_failed";
  }
  if (status === 429) {
    return "rate_limited";
  }
  return status >= 500 ? "internal_error" : "unknown";
}

function retryAfterOf(response: Response | undefined): number | null {
  const raw = response?.headers.get("Retry-After");
  if (!raw) {
    return null;
  }
  const seconds = Number(raw);
  if (Number.isFinite(seconds)) {
    return Math.max(0, Math.round(seconds));
  }
  const at = Date.parse(raw);
  return Number.isNaN(at) ? null : Math.max(0, Math.round((at - Date.now()) / 1000));
}

/** Build an ApiError from a failed response and its (already parsed) body. */
export function apiErrorFrom(response: Response | undefined, body: unknown): ApiError {
  const status = response?.status ?? 0;
  const envelope = (body ?? {}) as EnvelopeBody;
  const code =
    typeof envelope.error?.code === "string" && envelope.error.code
      ? envelope.error.code
      : statusCode(status);
  const details = Array.isArray(envelope.error?.details)
    ? (envelope.error.details as ApiErrorDetail[])
    : Array.isArray(envelope.detail)
      ? (envelope.detail as ApiErrorDetail[])
      : [];
  const requestId =
    typeof envelope.error?.requestId === "string"
      ? envelope.error.requestId
      : (response?.headers.get("X-Request-Id") ?? null);
  rememberRequestId(requestId);
  return new ApiError(status, code, details, requestId, retryAfterOf(response));
}

export interface FetchResultLike<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/** Return `data` or throw an ApiError (for openapi-fetch results). */
export async function unwrap<T>(request: Promise<FetchResultLike<T>>): Promise<T> {
  let result: FetchResultLike<T>;
  try {
    result = await request;
  } catch (error: unknown) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }
    throw new ApiError(0, "network_error");
  }
  if (!result.response.ok || result.data === undefined) {
    if (result.response.ok && result.response.status === 204) {
      return undefined as T;
    }
    throw apiErrorFrom(result.response, result.error);
  }
  return result.data;
}

/**
 * The field a validation detail belongs to. Services send `field` (camelCase) or the Pydantic
 * `loc` (["body", "price"]); both become the dotted form path used by react-hook-form.
 */
export function detailField(detail: ApiErrorDetail): string | null {
  if (typeof detail.field === "string" && detail.field) {
    return detail.field;
  }
  if (Array.isArray(detail.loc)) {
    const parts = detail.loc.filter((p, i) => !(i === 0 && (p === "body" || p === "query")));
    return parts.length ? parts.map(String).join(".") : null;
  }
  return null;
}
